import {
  Button,
  Container,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useEffect } from "react";
import { type Location, Navigate, useLocation, useNavigate } from "react-router-dom";

import { useCurrentUser, useLogin } from "@/lib/auth/hooks";

type LoginFormValues = {
  email: string;
  password: string;
};

type LoginLocationState = {
  from?: Location;
  adminRequired?: boolean;
};

function resolveRedirectPath(from: Location | undefined): string {
  if (from && from.pathname !== "/login") {
    return `${from.pathname}${from.search}${from.hash}`;
  }
  return "/dashboard";
}

/** Admin login screen backed by `POST /api/v1/auth/login`. */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LoginLocationState | null;
  const { data: user, isLoading: isUserLoading } = useCurrentUser();
  const login = useLogin();

  const form = useForm<LoginFormValues>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => (/^\S+@\S+\.\S+$/.test(value) ? null : "Enter a valid email address"),
      password: (value) => (value.length > 0 ? null : "Password is required"),
    },
  });

  useEffect(() => {
    if (locationState?.adminRequired) {
      notifications.show({
        id: "admin-access-required",
        title: "Access denied",
        message: "Admin access required.",
        color: "red",
      });
    }
  }, [locationState?.adminRequired]);

  if (isUserLoading) {
    return null;
  }

  if (user?.role === "admin") {
    return <Navigate to={resolveRedirectPath(locationState?.from)} replace />;
  }

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      navigate(resolveRedirectPath(locationState?.from), { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign in failed.";

      form.setFieldError("password", message);
      notifications.show({
        title: "Sign in failed",
        message,
        color: "red",
      });
    }
  });

  return (
    <Container size="xs" py="xl">
      <Paper withBorder p="xl" radius="md" shadow="sm">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Title order={2}>Pretor Admin</Title>
            <Text c="dimmed" size="sm">
              Sign in with your admin account to manage users and view platform statistics.
            </Text>
            <TextInput
              label="Email"
              placeholder="admin@example.com"
              type="email"
              autoComplete="email"
              required
              {...form.getInputProps("email")}
            />
            <PasswordInput
              label="Password"
              placeholder="Your password"
              autoComplete="current-password"
              required
              {...form.getInputProps("password")}
            />
            <Button type="submit" fullWidth loading={login.isPending}>
              Sign in
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
